import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import { searchEngineEventGetters } from '~/utils/searchEngineEventGetters'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import { convertMinutesToHumanString } from '~/utils/dateHelpers'

/** Which listing the card sits on; drops whichever of category/city the page already states. */
export type ProductCardContext = 'generic' | 'city' | 'category'
export type ProductCardTone = 'default' | 'tinted'
export type ProductCardTypography = 'standard' | 'quiet'
/**
 * Size of a card part (badge, wishlist, rating, price). `responsive` is
 * `small` below Tailwind's `md` and `default` from `md`, done in CSS so the
 * server and the client render the same markup.
 */
export type ProductCardPartSize = 'default' | 'small' | 'responsive'
/** Image chip: label, icon only, or icon only below `md` (`responsive`). */
export type ProductCardChipDisplay = 'label' | 'icon' | 'responsive'

export type ProductCardBadgeKind = 'sale' | 'box' | 'multidate'
export type ProductCardImageChipKind = 'accessible' | 'box'
/** Drives the tinted tone; mirrors the badge but also covers untagged online events. */
export type ProductCardKind = 'sale' | 'kit' | 'online' | 'plain'

export interface ProductCardBadge {
  kind: ProductCardBadgeKind
  label: string
}

export interface ProductCardImageChip {
  kind: ProductCardImageChipKind
  label: string
}

export interface ProductCardRating {
  value: string
  count: number
  /** "Partner rating" / "Google rating"; null for konfetti's own reviews. */
  sourceLabel: string | null
}

export interface ProductCardPrice {
  amount: string
  oldAmount: string | null
  unit: string | null
  isSale: boolean
}

export interface ProductCardV2Model {
  id: string
  /** konfetti's own giftcards: image, title and price only, like ProductCard. */
  isGiftcard: boolean
  title: string
  link: string
  image: string
  alt: string
  kind: ProductCardKind
  badge: ProductCardBadge | null
  imageChip: ProductCardImageChip | null
  contextLabel: string
  rating: ProductCardRating | null
  price: ProductCardPrice | null
  meta: string[]
  when: string | null
}

type Translate = (key: string, params?: Record<string, unknown>) => string

export interface ProductCardMapperOptions {
  t: Translate
  /** App locale (e.g. `de-de`), as `ProductCard` passes it to `formatPriceAmount`. */
  locale: string
  /** BCP47 locale for number/date formatting (e.g. `de-DE`). */
  localeBCP47: string
  context?: ProductCardContext
  /** Shows the "Course series" badge for `is_multidate` events. */
  extendedBadges?: boolean
  /** The special giftcard card in listings shows its price without "from". */
  isSpecialGiftcard?: boolean
}

const getBadge = (
  item: SearchEngineEventItem,
  t: Translate,
  extendedBadges: boolean
): ProductCardBadge | null => {
  if (searchEngineEventGetters.getHasPromotions(item)) {
    return { kind: 'sale', label: t('product.card.sale') }
  }
  // Giftcards only ever get the Sale badge.
  if (searchEngineEventGetters.isGiftcardEvent(item)) return null
  if (searchEngineEventGetters.isBoxEvent(item)) {
    return { kind: 'box', label: t('product.card.productBox') }
  }
  if (extendedBadges && item.is_multidate) {
    return { kind: 'multidate', label: t('product.card.multiDate') }
  }
  return null
}

/**
 * Something physical ships with the booking: the API's `required_address`,
 * which is being added to the Algolia/Typesense collections. Kits are boxes
 * themselves and get the Box badge instead.
 */
const getShipsBox = (item: SearchEngineEventItem): boolean =>
  !searchEngineEventGetters.isBoxEvent(item) &&
  searchEngineEventGetters.getRequiresAddress(item)

const getImageChip = (
  item: SearchEngineEventItem,
  t: Translate
): ProductCardImageChip | null => {
  if (searchEngineEventGetters.isGiftcardEvent(item)) return null
  if (searchEngineEventGetters.getIsAccessible(item)) {
    return { kind: 'accessible', label: t('product.card.accessible') }
  }
  if (getShipsBox(item)) {
    return { kind: 'box', label: t('product.card.boxIncluded') }
  }
  return null
}

const getKind = (item: SearchEngineEventItem): ProductCardKind => {
  if (searchEngineEventGetters.getHasPromotions(item)) return 'sale'
  if (searchEngineEventGetters.isGiftcardEvent(item)) return 'plain'
  if (searchEngineEventGetters.isBoxEvent(item)) return 'kit'
  if (searchEngineEventGetters.getIsOnline(item)) return 'online'
  return 'plain'
}

const joinLabel = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(' · ')

const getContextLabel = (
  item: SearchEngineEventItem,
  t: Translate,
  context: ProductCardContext
): string => {
  if (searchEngineEventGetters.isGiftcardEvent(item)) return ''

  const category = searchEngineEventGetters.fnGetMainCategory(item)

  // Kits and online events have no city, and most online events sit in an
  // "Online Kurs" category, so the category alone is enough.
  if (
    searchEngineEventGetters.isBoxEvent(item) ||
    searchEngineEventGetters.getIsOnline(item)
  ) {
    return joinLabel(category)
  }

  const city = searchEngineEventGetters.getCityName(item)

  switch (context) {
    case 'city':
      return joinLabel(category)
    case 'category':
      return joinLabel(city)
    default:
      return joinLabel(category, city)
  }
}

/** "Partner rating" / "Google rating"; null for konfetti's own reviews. */
export const getRatingSourceLabel = (
  item: SearchEngineEventItem,
  t: Translate
): string | null => {
  if (searchEngineEventGetters.getIsPartnerRating(item)) {
    return t('review.partnerRating.label')
  }
  if (searchEngineEventGetters.getIsThirdPartyRating(item)) {
    return t('review.thirdPartyRating.label')
  }
  return null
}

const getRating = (
  item: SearchEngineEventItem,
  t: Translate,
  localeBCP47: string
): ProductCardRating | null => {
  const value = searchEngineEventGetters.getAverageRating(item, localeBCP47)
  if (!value) return null

  const sourceLabel = getRatingSourceLabel(item, t)

  return {
    value: String(value),
    // Only konfetti's own reviews show a count; partner and Google ratings
    // show their source instead.
    count: sourceLabel ? 0 : searchEngineEventGetters.getRatingsAmount(item),
    sourceLabel,
  }
}

const getPriceUnit = (
  item: SearchEngineEventItem,
  t: Translate
): string | null => {
  if (searchEngineEventGetters.isGiftcardEvent(item)) return null
  if (searchEngineEventGetters.getIsGroupPricing(item)) {
    return t('product.card.price.groupPricingSuffix', {
      count: item.min_number_of_people,
    })
  }
  if (searchEngineEventGetters.hasShippingCosts(item)) {
    return t('product.show.checkout.shipping.productCardInfo')
  }
  // Like ProductCard: a plain per-person price needs no unit.
  return null
}

const getPrice = (
  item: SearchEngineEventItem,
  t: Translate,
  locale: string,
  isSpecialGiftcard: boolean
): ProductCardPrice | null => {
  const regular = searchEngineEventGetters.getRegularPriceAmount(item)
  if (!regular) return null

  const special = searchEngineEventGetters.getSpecialPriceAmount(item)
  const isSale = Boolean(special)
  const format = (amount: number) => formatPriceAmount(amount, locale, 'EUR', 0)
  const finalAmount = format(isSale ? Number(special) : regular)

  return {
    // Kits and the special giftcard have one fixed price, so no "from".
    amount:
      searchEngineEventGetters.isBoxEvent(item) || isSpecialGiftcard
        ? finalAmount
        : t('product.card.price.priceText', { price: finalAmount }),
    oldAmount: isSale ? format(regular) : null,
    unit: getPriceUnit(item, t),
    isSale,
  }
}

const getMeta = (item: SearchEngineEventItem): string[] => {
  const meta: string[] = []

  const duration = convertMinutesToHumanString(
    searchEngineEventGetters.fnGetDuration(item) ?? 0
  )
  if (duration) meta.push(duration)

  const languages = (item.languages ?? [])
    .map((language) => language.split('_')[0]?.toUpperCase())
    .filter(Boolean)
  if (languages.length) meta.push(languages.join('/'))

  const { min_number_of_people: min, max_number_of_people: max } = item
  if (min && max && min !== max) meta.push(`${min}–${max}`)
  else if (max) meta.push(String(max))

  return meta
}

const getWhen = (
  item: SearchEngineEventItem,
  localeBCP47: string
): string | null => {
  const next = searchEngineEventGetters.getNextDate(item)
  if (!next) return null

  return new Intl.DateTimeFormat(localeBCP47, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(next * 1000))
}

/** Maps a search hit onto everything `ProductCardV2` renders. */
export const mapProductCardV2 = (
  item: SearchEngineEventItem,
  {
    t,
    locale,
    localeBCP47,
    context = 'generic',
    extendedBadges = false,
    isSpecialGiftcard = false,
  }: ProductCardMapperOptions
): ProductCardV2Model => {
  const title = searchEngineEventGetters.fnGetTitle(item) || ''

  return {
    id: item.objectID,
    isGiftcard: searchEngineEventGetters.isGiftcardEvent(item),
    title,
    link: searchEngineEventGetters.fnGetLink(item),
    image: searchEngineEventGetters.getPrincipalImage(item),
    alt: searchEngineEventGetters.getPrincipalAlt(item) || title,
    kind: getKind(item),
    badge: getBadge(item, t, extendedBadges),
    imageChip: getImageChip(item, t),
    contextLabel: getContextLabel(item, t, context),
    rating: getRating(item, t, localeBCP47),
    price: getPrice(item, t, locale, isSpecialGiftcard),
    meta: getMeta(item),
    when: getWhen(item, localeBCP47),
  }
}

/** Card context for a listing page: drop whatever the page heading already states. */
export const getProductCardContext = (
  pageType: EventListPageType | undefined
): ProductCardContext => {
  switch (pageType) {
    case EventListPageType.CITY:
    case EventListPageType.CITY_CATEGORY:
      return 'city'
    case EventListPageType.CATEGORY:
      return 'category'
    default:
      return 'generic'
  }
}
